import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Star, Quote, ArrowLeft, ArrowRight, Play, Pause } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Testimonial {
  id: string;
  name: string;
  role: string;
  company: string;
  avatar: string;
  content: string;
  rating: number;
  videoUrl?: string;
  stats?: {
    timeSaved: string;
    wordsTyped: string;
  };
}

const testimonials: Testimonial[] = [
  {
    id: '1',
    name: 'Michael Rodriguez',
    role: 'Senior Developer',
    company: 'TechCorp',
    avatar: '👨‍💻',
    content: 'As a developer with RSI, SpeakFlow has been a game-changer. I can code by voice, write documentation, and respond to emails without pain. The accuracy is incredible!',
    rating: 5,
    stats: {
      timeSaved: '3+ hours/day',
      wordsTyped: '15,000+ words',
    },
  },
  {
    id: '2',
    name: 'Emily Watson',
    role: 'Content Writer',
    company: 'Creative Agency',
    avatar: '👩‍💼',
    content: 'I write 10,000+ words daily. SpeakFlow lets me draft articles while walking, cooking, or relaxing. My productivity has tripled and my writing feels more natural.',
    rating: 5,
    stats: {
      timeSaved: '4+ hours/day',
      wordsTyped: '50,000+ words',
    },
  },
  {
    id: '3',
    name: 'Dr. James Chen',
    role: 'Medical Professional',
    company: 'City Hospital',
    avatar: '👨‍⚕️',
    content: 'Patient notes that used to take hours now take minutes. The medical vocabulary recognition is outstanding. This should be standard in every hospital.',
    rating: 5,
    stats: {
      timeSaved: '2+ hours/day',
      wordsTyped: '8,000+ words',
    },
  },
  {
    id: '4',
    name: 'Sarah Johnson',
    role: 'PhD Student',
    company: 'Stanford University',
    avatar: '👩‍🎓',
    content: 'Writing my dissertation was destroying my wrists. SpeakFlow saved my academic career. The citation formatting and academic terminology support is perfect.',
    rating: 5,
    stats: {
      timeSaved: '5+ hours/day',
      wordsTyped: '20,000+ words',
    },
  },
  {
    id: '5',
    name: 'Robert Kim',
    role: 'Legal Counsel',
    company: 'Kim & Associates',
    avatar: '👨‍⚖️',
    content: 'Legal documents require precision. SpeakFlow handles complex legal terminology flawlessly. I\'ve reduced document preparation time by 70%.',
    rating: 5,
    stats: {
      timeSaved: '3+ hours/day',
      wordsTyped: '12,000+ words',
    },
  },
];

const floatingTestimonials = [
  { id: 'f1', text: 'Life changing!', author: '@techwriter' },
  { id: 'f2', text: 'Can\'t work without it', author: '@devlife' },
  { id: 'f3', text: 'RSI pain gone!', author: '@healthytyping' },
  { id: 'f4', text: '10x productivity', author: '@contentpro' },
  { id: 'f5', text: 'Worth every penny', author: '@smartinvest' },
];

export function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section id="testimonials" className="py-24 relative overflow-hidden">
      {/* Floating Testimonial Bubbles */}
      <div className="absolute inset-0 pointer-events-none">
        {floatingTestimonials.map((testimonial, index) => (
          <div
            key={testimonial.id}
            className={cn(
              "absolute bg-card border rounded-full px-4 py-2 shadow-lg",
              "animate-float opacity-0",
              index % 2 === 0 ? "left-10" : "right-10"
            )}
            style={{
              top: `${20 + index * 15}%`,
              animationDelay: `${index * 1.5}s`,
              animationDuration: `${15 + index * 2}s`,
              animationFillMode: 'forwards',
            }}
          >
            <p className="text-sm font-medium">{testimonial.text}</p>
            <p className="text-xs text-muted-foreground">{testimonial.author}</p>
          </div>
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <Quote className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">Success Stories</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold">
              Join 50,000+ Happy Users
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Real people, real results. See how SpeakFlow is transforming the way people work.
            </p>
          </div>

          {/* Main Testimonial Carousel */}
          <div className="relative max-w-4xl mx-auto mb-16">
            <Card className="overflow-hidden">
              <CardContent className="p-8 lg:p-12">
                <div className="grid lg:grid-cols-[1fr,auto] gap-8 items-center">
                  <div className="space-y-6">
                    {/* Rating */}
                    <div className="flex items-center gap-1">
                      {[...Array(currentTestimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 text-yellow-500 fill-current" />
                      ))}
                    </div>

                    {/* Quote */}
                    <blockquote className="text-lg lg:text-xl italic relative">
                      <Quote className="absolute -top-2 -left-2 h-8 w-8 text-primary/20" />
                      <p className="relative z-10">{currentTestimonial.content}</p>
                    </blockquote>

                    {/* Author */}
                    <div className="flex items-center gap-4">
                      <div className="text-4xl">{currentTestimonial.avatar}</div>
                      <div>
                        <p className="font-semibold">{currentTestimonial.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {currentTestimonial.role} at {currentTestimonial.company}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Stats */}
                  {currentTestimonial.stats && (
                    <div className="lg:border-l lg:pl-8 space-y-4">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-primary">
                          {currentTestimonial.stats.timeSaved}
                        </p>
                        <p className="text-sm text-muted-foreground">Time Saved</p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-primary">
                          {currentTestimonial.stats.wordsTyped}
                        </p>
                        <p className="text-sm text-muted-foreground">Words Created</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-6">
              <div className="flex gap-2">
                <button
                  onClick={goToPrevious}
                  className="p-2 rounded-lg hover:bg-accent transition-colors"
                  aria-label="Previous testimonial"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={goToNext}
                  className="p-2 rounded-lg hover:bg-accent transition-colors"
                  aria-label="Next testimonial"
                >
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>

              {/* Dots */}
              <div className="flex gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setCurrentIndex(index);
                      setIsAutoPlaying(false);
                    }}
                    className={cn(
                      "w-2 h-2 rounded-full transition-all",
                      index === currentIndex
                        ? "w-8 bg-primary"
                        : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                    )}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>

              {/* Auto-play toggle */}
              <button
                onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                className="p-2 rounded-lg hover:bg-accent transition-colors"
                aria-label={isAutoPlaying ? "Pause auto-play" : "Resume auto-play"}
              >
                {isAutoPlaying ? (
                  <Pause className="h-5 w-5" />
                ) : (
                  <Play className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center">
            <p className="text-lg text-muted-foreground mb-6">
              Ready to transform your productivity?
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button className="group relative px-6 py-3 font-medium">
                <span className="relative z-10 flex items-center gap-2">
                  Read More Reviews
                  <span className="transition-transform group-hover:translate-x-1">→</span>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}